/**
 * THE IDLE CLOCK THE SERVER SEES.
 *
 * ===========================================================================
 *  A MEASUREMENT SLOT HOLDS ONLY MEASUREMENTS.
 *
 *  Every socket, every document, every path. A document that has measured
 *  nothing must never vote for a human - not by a frame, not by a ping, not
 *  by arriving, not by a seed, not by a failed read, not by becoming visible.
 *
 *  THERE ARE ONLY THREE WAYS TO REACH THE SERVER'S DECISION, and each clause
 *  above is one door that was found open on one of them. Round 1 fixed the
 *  flags, round 2 the frames, round 3 the pings and the arrival - each round
 *  believing it had finished, because nothing enumerated the doors. So:
 *
 *    THE FRAME    `dispatch.ts case 'presence'` -> applyActivity.
 *                 ONE call site, and it must stay one:
 *                 features/presence/systemActivity.ts `reportPresence`, gated
 *                 by `framesFor(claim)` on `measured` and `observed`.
 *    THE PING     `dispatch.ts handlePing` -> applyActivity(idle < autoAwayMs).
 *                 TWO call sites, both `startHeartbeat` below, both reading
 *                 `idleMs()` - which is this file, and is why this file exists.
 *    THE ARRIVAL  `redis/presence.ts onConnect` -> `hincrby(k, "a", 1)`.
 *                 EVERY `realtime.connect()` in the app (there are ten), which
 *                 is exactly why its retraction is not at any call site: the
 *                 handshake's own first ping covers all of them by
 *                 construction. See `startHeartbeat`.
 *
 *  AND THE LOCK HAS DOORS OF ITS OWN, which the list above never covered: it
 *  enumerates how a CLAIM reaches the server, not how a RETRACTION reaches
 *  each clock. The lock reaches a clock through `setAbsent` alone, written by
 *  features/presence/systemActivity.ts `applySystemActivity` - which runs only
 *  where `PresenceRuntime` is mounted, because the desktop shell sends its OS
 *  reports to the main window and nowhere else. A desktop call window is deaf
 *  to it. So, beside the rule above:
 *
 *    NO DOCUMENT MAY HOLD EVIDENCE THAT A SIGNAL IT CANNOT HEAR WOULD RETRACT.
 *
 *    A KEYSTROKE  `typing()` was the one path by which a document that
 *                 declared nothing could acquire evidence, and it broke that
 *                 rule: one character in a call window's chat, Win+L two
 *                 seconds later, and that window's pings told the gateway
 *                 "two seconds" - Available to every colleague for five
 *                 minutes on a locked machine. It writes through `noteInput`,
 *                 which records only where the entitlement was granted, and
 *                 the entitlement is granted by the same declaration that makes
 *                 a document hear the lock (`measurePresenceHere`). Nothing is
 *                 lost: the shell reads idle across the whole OS, so that
 *                 keystroke reaches the workspace's clock as a measurement.
 *    A RESET      `forget()` drops the lock along with the person's evidence,
 *                 and a locked machine pushes nothing to restore it, so
 *                 `PresenceRuntime` re-reads the machine from the shell
 *                 straight after every reset it makes.
 *
 *  test/systemActivity.test.mjs holds this list against the code.
 * ===========================================================================
 *
 * `createRealtimeClient` puts `idleMs()` on every `ping`, and the gateway
 * decides Away from it directly - `handlePing` calls
 * `applyActivity(conn, idle < conn.autoAwayMs)`, which sets or clears the flag
 * every colleague's roster is drawn from. So this number is a claim about a
 * human, and the rules below are the whole of what makes it honest.
 *
 * SILENCE IS A VOTE, AND THAT IS WHY THIS FILE CANNOT SIMPLY ABSTAIN.
 *
 * The gateway counts a socket ACTIVE from the moment it arrives
 * (`redis/presence.ts onConnect`: `hincrby(k, "a", 1)`, `hset(k, "st",
 * "available")`) and resolves the PERSON across all of their sockets with
 * `activeCount > 0 ? "available" : "away"`. So the most optimistic connection
 * answers for everybody, and a document with nothing to say does not abstain
 * by saying nothing - it leaves its own arrival standing as a claim that
 * somebody is at the keyboard. It must RETRACT that arrival, and the only way
 * it can is to put `IDLE_ABSENT_MS` on its ping.
 *
 * That is why `idleMs()` has NO FALLBACK. Two rounds fixed the flags and then
 * the frames and both left this line:
 *
 *     return now - (measuredAt === 0 ? openedAt : measuredAt);
 *
 * A desktop CALL WINDOW mounts `RealtimeRuntime` and not `PresenceRuntime`, so
 * it has no DOM monitor, no OS reports and no tick - it never measures
 * anything at all. `framesFor` was taught to keep it silent and its 25 s
 * heartbeat went on sending `now - openedAt` from that fallback, which the
 * gateway cannot tell from a measurement. Lock the workstation with a call
 * window open and every colleague saw AVAILABLE for five minutes, and again
 * for 25 s after every reconnect, for ever. Executed, three times, by three
 * rounds. The fallback is gone: no evidence is `IDLE_ABSENT_MS`.
 *
 * So the FOUR kinds of thing this clock can be told are kept apart:
 *
 *   OBSERVED  `note(at)` - DOM input into this document, or the desktop shell's
 *             reading of `getSystemIdleTime()`. Only these are measurements.
 *   ASSUMED   `assumeOpenedByHuman()` - somebody loaded this page, so somebody
 *             was probably here. AN ENTITLEMENT THAT MUST BE GRANTED, not a
 *             property of every clock: the only caller is
 *             `features/presence/systemActivity.ts measurePresenceHere()`, i.e.
 *             the document that watches its user says so. A fixed instant,
 *             never refreshed, which loses to the first measurement rather than
 *             competing with it.
 *   STATED    `setAbsent()` - the OS says the workstation is locked. Not a
 *             duration and not a measurement: a statement that outranks both,
 *             because the last thing a person does before walking away is press
 *             a key, so the measurement says they are here and the measurement
 *             is right and useless.
 *   NOTHING   the default. `IDLE_ABSENT_MS`, which retracts the arrival.
 *
 * IT IS A MODULE OF ITS OWN so it can be executed. The transport around it
 * needs a WebSocket, a token, a document and a network to instantiate at all,
 * and a rule that can only be exercised through all of that is a rule nobody
 * checks - which is how the seed below survived a mutation run while every
 * suite stayed green. It is the same split the shell uses: the rules in a
 * dependency-free module (apps/desktop/src/shared/systemIdle.ts), the wiring
 * thin enough to read.
 */

/**
 * GONE. What `idleMs()` answers while the workstation is locked.
 *
 * A sentinel rather than a duration measured from the lock, because "locked" is
 * not a length of time: five seconds after locking, the honest answer to "how
 * long since a human was here" is five seconds, and that is exactly the answer
 * that painted a green dot on an empty desk 25 s after Win+L. Any threshold a
 * user can configure fits in `conn.autoAwayMs`, and all of them are smaller
 * than this, so `idle < conn.autoAwayMs` is false whatever they chose.
 *
 * It is the same sentinel `presenceStore.idleSince()` already answers for the
 * same fact, so the two clocks express absence identically rather than by
 * coincidence.
 */
export const IDLE_ABSENT_MS = Number.MAX_SAFE_INTEGER;

export interface IdleClock {
  /**
   * A human WAS OBSERVED here at `at` (default: now).
   *
   * MEASUREMENTS ONLY. Two sources write it and they answer different
   * questions — DOM input into this document, and the desktop shell's reading
   * of the OS — so it is FORWARD ONLY: the shell's sample can be up to one poll
   * (2 s) old and its event bus can hold a report back across a reload, and an
   * older reading must never overwrite a keystroke that landed after it.
   *
   * Nothing that merely BELIEVES a human is here may call this. A reconnect, a
   * page load and a restored window are beliefs; see the file header.
   */
  note(at?: number): void;
  /**
   * A KEYSTROKE this document sent - a measurement, recorded only where the
   * lock can reach it.
   *
   * `typing()` is the caller. In the document a person opened it is the best
   * evidence there is; in one that never declared `measurePresenceHere` - a
   * desktop call window - nothing will ever tell this clock that the machine
   * locked, so a keystroke there would outlive Win+L by the whole away
   * threshold. See the file header.
   */
  noteInput(at?: number): void;
  /**
   * The OS says the workstation is locked, or is no longer.
   *
   * Outranks every measurement while it is set, and takes nothing away when it
   * is cleared: unlocking is itself input, so the shell's very next reading
   * carries the measurement that makes this user Available again.
   */
  setAbsent(absent: boolean): void;
  /**
   * A HUMAN OPENED THIS DOCUMENT - so this clock may fall back on saying so.
   *
   * THE ENTITLEMENT IS GRANTED, NEVER ASSUMED, and that is the whole of the
   * repair. Every clock used to carry the page-load seed whether or not
   * anything in its document would ever look at a human, so the one document
   * that measures nothing - a desktop call window - spent five minutes
   * reporting a keyboard nobody was at, on its own socket, which outvoted the
   * workspace's honest `idle` because the gateway aggregates.
   *
   * `measurePresenceHere()` is the only caller: the same declaration that makes
   * `presenceClaim().measured` true. A document that does not watch its user
   * does not get to guess about them.
   *
   * IT IS NOT REFRESHED. The instant is `openedAt` and stays `openedAt`, so the
   * guess AGES and expires at the user's own threshold, and any real
   * measurement replaces it outright rather than competing with it.
   */
  assumeOpenedByHuman(): void;
  /**
   * BACK TO KNOWING NOTHING — every measurement and the lock with them.
   *
   * The document is still the same document, so `openedAt` stands and this
   * clock answers from the assumption again. Called when the PERSON this
   * client describes may have changed (`features/presence/systemActivity.ts`),
   * beside the identical wipe of the local clock in `presenceStore`: they are
   * one piece of state about one person held in two modules, and a reset that
   * cleared one of them would leave the other describing somebody who is not
   * there — which is the whole failure mode this file is about, in miniature.
   *
   * The lock goes too, although it describes the MACHINE and the machine did
   * not change - which is only correct because the caller asks the shell what
   * the machine is doing straight afterwards (`PresenceRuntime`).
   */
  forget(): void;
  /** What to put on the next `ping`. */
  idleMs(now?: number): number;
}

/**
 * `openedAt` is the ASSUMPTION this clock may be entitled to - that whoever
 * loaded the page is at the machine. It is a reasonable default in the document
 * a human opened and a fatal floor everywhere else.
 *
 * The value used to be written straight into the clock, and that made the
 * assumption indistinguishable from a measurement. A document reloaded beside
 * an empty desk then out-voted the shell's "the last input to this machine was
 * fifty minutes ago" - and went on out-voting it for ever, because OS idle only
 * grows while nobody is there, so every later reading lost to the same seed.
 * The user read Available to the whole workspace until they came back.
 *
 * Holding it separately keeps the difference: the first real reading REPLACES
 * it rather than competing with it. Holding it UNGRANTED keeps the second
 * difference, which cost this feature two more rounds: a document that will
 * never measure anything never gets to fall back on it either.
 */
export function createIdleClock(openedAt: number = Date.now()): IdleClock {
  let measuredAt = 0;
  let absent = false;
  /** Ungranted until a document that watches its user says otherwise. */
  let assumed = false;

  const note = (at?: number): void => {
    const when = at ?? Date.now();
    if (when > measuredAt) measuredAt = when;
  };

  return {
    note,
    noteInput(at) {
      /* No entitlement, no lock - and no evidence. See the file header. */
      if (assumed) note(at);
    },
    setAbsent(next) {
      absent = next;
    },
    assumeOpenedByHuman() {
      assumed = true;
    },
    forget() {
      measuredAt = 0;
      absent = false;
      /* The ENTITLEMENT survives, because it describes this DOCUMENT and not
         the person in front of it - the same reason `measuring` survives the
         matching reset in `systemActivity.ts`. What it dates from does not
         move, so whoever signs in next inherits a guess that is already as old
         as the tab and expires on schedule rather than starting again. */
    },
    idleMs(now = Date.now()) {
      if (absent) return IDLE_ABSENT_MS;
      /*
       * NO FALLBACK. In this order: a statement, then a measurement, then a
       * granted assumption, then nothing at all - and NOTHING IS NOT ZERO. It
       * is `IDLE_ABSENT_MS`, which is what retracts this socket's arrival.
       */
      if (measuredAt !== 0) return now - measuredAt;
      if (assumed) return now - openedAt;
      return IDLE_ABSENT_MS;
    },
  };
}
